"use client"

import type React from "react"

import { useState, useRef, useEffect } from "react"
import { ChevronDown, ChevronRight, Database, ExternalLink } from "lucide-react"
import { cn } from "@/lib/utils"

export interface Citation {
  id: number
  memoryId: string
  memoryLabel: string
  memoryValue: string
  dashboardSlug: string
  sectionSlug: string
  relevanceScore: number
}

interface CitationsPanelProps {
  citations: Citation[]
  className?: string
  onCitationClick?: (citation: Citation) => void
  highlightedCitationId?: number | null
}

export function CitationsPanel({ citations, className, onCitationClick, highlightedCitationId }: CitationsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const highlightedRef = useRef<HTMLDivElement>(null)

  // Auto-expand and scroll to highlighted citation
  useEffect(() => {
    if (highlightedCitationId !== null && highlightedCitationId !== undefined) {
      setIsExpanded(true)
      setTimeout(() => {
        highlightedRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
      }, 100)
    }
  }, [highlightedCitationId])

  if (citations.length === 0) return null

  return (
    <div className={cn("mt-3 pt-3 border-t border-border/20", className)}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />
        )}
        <Database className="h-3.5 w-3.5 text-brain" strokeWidth={1.5} />
        <span className="font-medium">
          {citations.length} memory source{citations.length !== 1 ? "s" : ""} referenced
        </span>
      </button>

      {isExpanded && (
        <div className="mt-2 space-y-2">
          {citations.map((citation) => (
            <CitationItem
              key={citation.id}
              ref={highlightedCitationId === citation.id ? highlightedRef : null}
              citation={citation}
              isHighlighted={highlightedCitationId === citation.id}
              onClick={() => onCitationClick?.(citation)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface CitationItemProps {
  citation: Citation
  isHighlighted?: boolean
  onClick?: () => void
}

import { forwardRef } from "react"

const CitationItem = forwardRef<HTMLDivElement, CitationItemProps>(function CitationItem(
  { citation, isHighlighted, onClick },
  ref,
) {
  const [showFull, setShowFull] = useState(false)

  // Format dashboard/section path
  const sourcePath = `${citation.dashboardSlug} / ${citation.sectionSlug}`

  return (
    <div
      ref={ref}
      className={cn(
        "p-2.5 rounded-lg text-xs transition-all duration-200 cursor-pointer",
        isHighlighted
          ? "bg-brain/10 border border-brain/30 ring-1 ring-brain/20"
          : "bg-surface-elevated/30 hover:bg-surface-elevated/50 border border-transparent",
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-2">
        {/* Citation number badge */}
        <span
          className={cn(
            "shrink-0 w-5 h-5 rounded-full flex items-center justify-center font-medium text-[10px]",
            isHighlighted ? "bg-brain text-white" : "bg-brain/20 text-brain",
          )}
        >
          {citation.id}
        </span>

        <div className="flex-1 min-w-0">
          {/* Header row with label and path */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-medium text-foreground truncate">{citation.memoryLabel}</span>
            <span className="text-[10px] text-muted-foreground shrink-0 flex items-center gap-1">
              {sourcePath}
              <ExternalLink className="h-2.5 w-2.5" strokeWidth={1.5} />
            </span>
          </div>

          {/* Memory value preview */}
          <p className="text-muted-foreground leading-relaxed">
            {showFull
              ? citation.memoryValue
              : citation.memoryValue.slice(0, 100) + (citation.memoryValue.length > 100 ? "..." : "")}
          </p>

          {/* Expand/collapse and relevance score */}
          <div className="flex items-center justify-between mt-1.5">
            {citation.memoryValue.length > 100 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowFull(!showFull)
                }}
                className="text-brain hover:text-brain/80 text-[10px]"
              >
                {showFull ? "Show less" : "Show more"}
              </button>
            )}
            <span className="text-[10px] text-muted-foreground ml-auto">
              {Math.round(citation.relevanceScore * 100)}% relevant
            </span>
          </div>
        </div>
      </div>
    </div>
  )
})

/**
 * Inline citation marker component for use in message text
 * Clickable markers that scroll to and highlight the corresponding citation
 */
export function CitationMarker({
  id,
  onClick,
  isActive,
}: {
  id: number
  onClick?: () => void
  isActive?: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-medium transition-all mx-0.5 align-super",
        isActive ? "bg-brain text-white scale-110" : "bg-brain/20 text-brain hover:bg-brain/30",
      )}
      title={`View source ${id}`}
    >
      {id}
    </button>
  )
}

/**
 * Parse message content and inject citation markers
 * Looks for patterns like [1], [2], etc. and replaces with clickable markers
 */
export function parseMessageWithCitations(
  content: string,
  onCitationClick: (id: number) => void,
  activeCitationId?: number | null,
): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  const citationRegex = /\[(\d+)\]/g
  let lastIndex = 0
  let match

  while ((match = citationRegex.exec(content)) !== null) {
    // Add text before the citation
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index))
    }

    // Add citation marker
    const citationId = Number.parseInt(match[1], 10)
    parts.push(
      <CitationMarker
        key={`citation-${match.index}`}
        id={citationId}
        onClick={() => onCitationClick(citationId)}
        isActive={activeCitationId === citationId}
      />,
    )

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex))
  }

  return parts
}
